import { useEffect, useState } from "react";
import Card from "./components/ui/Card";
import BaseTemplate from "./templates/BaseTemplate";
import {
  createInterpretationTheme,
  getInterpretationThemes,
  associateInterpretationThemesToDiagnosticPattern,
} from "./api";
import { NavLink, useNavigate, useParams } from "react-router";
import Checkbox from "./components/ui/Checkbox";
import Button from "./components/ui/Button";
import { Plus } from "lucide-react";
import BaseModal from "./components/ui/BaseModal";
import { AnimatePresence, motion } from "framer-motion";
import Input from "./components/ui/Input";
import Textarea from "./components/ui/Textarea";
import { getUniqueBrainAreasFromChannels } from "./lib/utils";
import BadgeBrainRegion from "./components/ui/BadgeBrainRegion";
import Badge from "./components/ui/Badge";

const InterpretationThemes = () => {
  const [interpretationThemes, setInterpretationThemes] = useState([]);
  const [associatedThemes, setAssociatedThemes] = useState([]);
  let { patternId } = useParams();
  let navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [newTheme, setNewTheme] = useState({
    interpretation_text: "",
    keywords: "",
    source_type: "",
    cited_references: "",
    theme_involved_channels: "",
    theme_involved_bands: "",
    theme_deviation_types: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInterpretationThemes = async () => {
    try {
      const response = await getInterpretationThemes();
      setInterpretationThemes(response.data || []);
    } catch (err) {
      console.error("Error fetching patients:", err);
      // setError(err.response?.data?.detail || "Failed to fetch patients");
      setInterpretationThemes([]);
    }
  };

  useEffect(() => {
    fetchInterpretationThemes();
  }, []);

  const onSelectTheme = (checked, id) => {
    if (checked) {
      setAssociatedThemes((v) => [...v, id]);
    } else {
      setAssociatedThemes(associatedThemes.filter((theme) => theme !== id));
    }
  };

  const onAssociateThemes = async () => {
    console.log(associatedThemes);

    try {
      await associateInterpretationThemesToDiagnosticPattern(
        patternId,
        associatedThemes
      );
      alert("Themes associated successfully!");
      navigate(`/diagnostics-center/patterns/${patternId}`);
    } catch (err) {
      console.error("Error linking interpretation themes:", err);
      setError(err.response?.data?.detail || err.message);
    }
  };

  const headers = patternId
    ? [
        "Select",
        "ID",
        "Description",
        "Source Type",
        "Keywords",
        "Created At",
        "Regions",
      ]
    : ["ID", "Description", "Source Type", "Keywords", "Created At", "Regions"];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTheme((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const themeToCreate = {
        ...newTheme,
        keywords: newTheme.keywords.split(","),
        source_type: "ClinicianJudgment",
        cited_references: "",
        theme_involved_channels: newTheme.theme_involved_channels.split(","),
        theme_involved_bands: newTheme.theme_involved_bands.split(","),
        theme_deviation_types: [],
      };

      console.log(themeToCreate);

      await createInterpretationTheme(themeToCreate);
      setNewTheme({
        interpretation_text: "",
        keywords: "",
        source_type: "",
        cited_references: "",
        theme_involved_channels: "",
        theme_involved_bands: "",
        theme_deviation_types: "",
      });
    } catch (err) {
      console.error("Error creating interpretation theme:", err);
      setError(err.response?.data?.detail || err.message);
    }
    setIsLoading(false);
  };

  return (
    <BaseTemplate>
      <Card name={"Interpretation Themes"} className={""}>
        {patternId && (
          <div className="flex flex-row items-center justify-between gap-2">
            <h3 className="text-lg text-[#1c3f70] font-semibold">
              Selected themes to associate: {associatedThemes.length}
            </h3>
            <Button
              onClick={onAssociateThemes}
              className="text-white px-3 py-1 md:px-4 md:py-1.5 self-end"
              disabled={associatedThemes.length === 0}
            >
              <Plus />
              Associate Selected Themes
            </Button>
          </div>
        )}
        <div className="flex mt-2">
          <Button
            onClick={() => navigate("/interpretation-themes/add")}
            className="text-white px-3 py-1 md:px-4 md:py-1.5 ml-auto"
          >
            <Plus />
            Add Theme
          </Button>
        </div>
        <table className="w-full mt-2">
          <thead>
            <tr>
              {headers.map((name) => (
                <th key={name} className="p-2 text-left text-base font-medium ">
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {interpretationThemes.map((interpretationTheme, index) => (
              <tr
                key={interpretationTheme.id}
                className="border-t border-b border-gray-100 even:bg-gray-100 transition-colors"
              >
                {patternId && (
                  <td className="p-2">
                    <Checkbox
                      className={"mx-auto"}
                      cb={(checked) =>
                        onSelectTheme(checked, interpretationTheme.id)
                      }
                    ></Checkbox>
                  </td>
                )}
                <td className="p-2 underline decoration-blue-500">
                  <NavLink
                    to={`/interpretation-themes/themes/${interpretationTheme.id}`}
                  >
                    {interpretationTheme.id}
                  </NavLink>
                </td>
                <td className="p-2">
                  <span className="line-clamp-5">
                    {interpretationTheme.interpretation_text}
                  </span>
                </td>
                <td className="p-2">{interpretationTheme.source_type}</td>
                <td className="p-2">
                  {interpretationTheme.keywords &&
                    interpretationTheme.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {interpretationTheme.keywords
                          .slice(0, 3)
                          .map((keyword) => {
                            if (keyword !== "") {
                              return (
                                <Badge
                                  variant={
                                    index % 2 !== 0 ? "white" : "default"
                                  }
                                  key={keyword}
                                >
                                  {keyword}
                                </Badge>
                              );
                            } else return null;
                          })}
                        {interpretationTheme.keywords.length > 3 && (
                          <span
                            title={interpretationTheme.keywords
                              .slice(3)
                              .join(", ")}
                          >{`+${interpretationTheme.keywords.length - 3} more`}</span>
                        )}
                      </div>
                    )}
                </td>
                <td className="p-2">
                  {new Date(interpretationTheme.created_at).toLocaleString()}
                </td>
                <td className="p-2">
                  <div className="flex flex-row flex-wrap gap-2">
                    {getUniqueBrainAreasFromChannels(
                      interpretationTheme.theme_involved_channels ?? []
                    ).map((brainArea) => (
                      <BadgeBrainRegion
                        key={`${index}-${brainArea}`}
                        region={brainArea}
                      />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <BaseModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title="Add Interpretation Theme"
        >
          <AnimatePresence>
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <Textarea
                label="Text"
                onChange={handleInputChange}
                name="text"
                value={newTheme.text}
                placeholder="Description of the theme"
                required
                rows={2}
                className="text-sm"
              />
              <Input
                label="Keywords"
                type="text"
                onChange={handleInputChange}
                name="keywords"
                value={newTheme.keywords}
                placeholder="e.g. anxiety,executive function"
                className="mt-3 w-full"
              />
              <Input
                label="Involved Channels"
                type="text"
                onChange={handleInputChange}
                name="theme_involved_channels"
                value={newTheme.theme_involved_channels}
                placeholder="F3,F4"
                required
                className="mt-3 w-full"
              />
              <Input
                label="Involved Bands"
                type="text"
                onChange={handleInputChange}
                name="theme_involved_bands"
                value={newTheme.theme_involved_bands}
                placeholder="delta,theta"
                required
                className="mt-3 w-full"
              />
              <div className="flex space-x-2 pt-1 md:pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className={`px-3 py-1.5 md:px-5 md:py-2 bg-gradient-to-r from-[#2a5caa] to-[#3963aa] text-white rounded-lg text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all ${
                    isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Creating..." : "Create Theme"}
                </Button>
              </div>
              {error && (
                <motion.div
                  className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-red-600 text-xs md:text-sm">
                    {JSON.stringify(error)}
                  </p>
                </motion.div>
              )}
            </form>
          </AnimatePresence>
        </BaseModal>
      </Card>
    </BaseTemplate>
  );
};

export default InterpretationThemes;
